import React, { useRef, useState } from "react";
import { Paperclip, Trash2, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * iter72k · Shared attachment picker used by both the initial-submit
 * modal and the reply box on ticket detail. Pre-uploads each file to
 * /api/support/attachments and hands the caller a list of
 * attachment_ids to send with their next POST.
 */
const MAX_FILES = 5;
const MAX_BYTES = 25 * 1024 * 1024;
const ALLOWED_MIME = new Set([
  "image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "application/pdf",
]);

export default function SupportAttachmentPicker({
  attachments, setAttachments, testIdPrefix = "support-attach",
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = "";
    for (const f of files) {
      if (attachments.length + 1 > MAX_FILES) {
        setErr(`Up to ${MAX_FILES} files per action.`);
        break;
      }
      if (!ALLOWED_MIME.has(f.type)) {
        setErr(`"${f.name}" — images or PDF only.`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        setErr(`"${f.name}" is over the 25 MB limit.`);
        continue;
      }
      setUploading(true); setErr("");
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/support/attachments", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((prev) => [...prev, data]);
      } catch (e2) {
        setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
      } finally { setUploading(false); }
    }
  };

  const remove = (id) => setAttachments((prev) => prev.filter((a) => a.attachment_id !== id));

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A]">
          Attachments ({attachments.length}/{MAX_FILES})
        </span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={attachments.length >= MAX_FILES || uploading}
          data-testid={`${testIdPrefix}-btn`}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#1A422D] hover:text-[#0f1a14] disabled:opacity-50"
        >
          {uploading ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />}
          {uploading ? "Uploading…" : "Attach"}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/webp,image/gif,image/heic,application/pdf"
          onChange={onFiles}
          data-testid={`${testIdPrefix}-input`}
          className="hidden"
        />
      </div>
      {attachments.length > 0 && (
        <ul className="space-y-1" data-testid={`${testIdPrefix}-list`}>
          {attachments.map((a) => (
            <li key={a.attachment_id}
                className="flex items-center gap-2 border border-[#E5E3D8] bg-[#FBF9F1] px-2 py-1 text-xs">
              <Paperclip size={11} className="text-[#8a6f33]" />
              <span className="truncate flex-1">{a.filename}</span>
              <span className="text-[10px] text-[#7A7A7A]">{formatSize(a.size)}</span>
              <button type="button" onClick={() => remove(a.attachment_id)}
                      data-testid={`${testIdPrefix}-remove-${a.attachment_id}`}
                      className="text-[#8a1a1a] hover:text-[#a33]">
                <Trash2 size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {err && <p className="text-[11px] text-[#8a1a1a] mt-1">{err}</p>}
    </div>
  );
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
